// src/utils/sentry.ts
//
// Bundled Sentry initialization. This replaces the previous
// `<script src="https://js.sentry-cdn.com/...loader.min.js">` +
// inline `Sentry.onLoad(...)` setup that lived in the HTML templates.
//
// The CDN loader was blocked by common ad-blockers / Firefox Enhanced
// Tracking Protection, which caused a `ReferenceError: Sentry is not
// defined` from the inline `Sentry.onLoad(...)` call and produced noisy
// console errors on every page load. Bundling `@sentry/browser` avoids the
// third-party CDN request entirely, so Sentry works even when the loader
// origin is blocked.
//
// If `SENTRY_DSN` is not provided at build time the initializer is a no-op
// (safe default — nothing crashes, no telemetry is sent).

import * as Sentry from "@sentry/browser";

import { SENTRY_DSN, SENTRY_ENVIRONMENT } from "./config";

let initialized = false;

export function initSentry(): void {
	if (initialized) return;
	initialized = true;

	if (!SENTRY_DSN) {
		// No DSN configured — skip init silently. Prevents any accidental
		// no-op init from wiring up global handlers.
		return;
	}

	try {
		Sentry.init({
			dsn: SENTRY_DSN,
			environment: SENTRY_ENVIRONMENT || "development",
			tracesSampleRate: 1.0,
			replaysSessionSampleRate: 0.0,
			replaysOnErrorSampleRate: 1.0,
			ignoreErrors: [
				// Facebook / Instagram in-app browser injected bridge code
				/window\.webkit\.messageHandlers/i,
				"undefined is not an object (evaluating 'window.webkit.messageHandlers')",
				// Other known in-app browser / bridge noise
				/_AutofillCallbackHandler/i,
				"instantSearchSDKJSBridgeClearHighlight",
			],
		});
	} catch (err) {
		// Never let Sentry init break the app.
		// eslint-disable-next-line no-console
		console.warn("Sentry init failed:", err);
	}
}
